import { Component, OnInit } from '@angular/core'
import { MatDialogRef } from '@angular/material/dialog'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import {
  AddUser,
  countries,
  genders,
  languages,
  Destroyable,
  isEmailTaken,
  notAllNumbersValidator,
  ParsedGooglePlace,
  SaveForm,
  selectRoles,
  selectUsers,
  Toaster,
  untilDestroy,
  User,
  UserSettingsForm,
  USER_ROLES,
} from '@tokeet/tv3-platform'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import * as R from 'ramda'
import { MapsAPILoader } from '@agm/core'
import { selectActiveAddonPlanViews, selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { alphanumericNameValidator } from '@tokeet-frontend/utility'

@Component({
  selector: 'app-add-user-dialog',
  templateUrl: './add-user-dialog.component.html',
  styleUrls: ['./add-user-dialog.component.scss'],
})
export class AddUserDialogComponent extends Destroyable implements OnInit {
  users: User[]

  countryList = countries
  languages = languages
  genders = genders
  roles = USER_ROLES

  isYearlyOC = false

  roles$ = this.store.pipe(select(selectRoles))

  form = this.fb.group({
    firstName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        notAllNumbersValidator,
        alphanumericNameValidator,
      ],
    ],
    lastName: ['', [Validators.required, alphanumericNameValidator]],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    address: ['', [Validators.minLength(2), Validators.maxLength(140)]],
    city: ['', [Validators.minLength(2), Validators.maxLength(50)]],
    state: ['', [Validators.minLength(2), Validators.maxLength(50)]],
    country: [''],
    password1: ['', [Validators.required, Validators.minLength(8)]],
    password2: ['', [Validators.required, Validators.minLength(8)]],
    role: ['', [Validators.required]],
    attributes: this.fb.group({
      gender: [],
      language: [],
    }),
  })
  isGoogleLoaded = false

  constructor(
    public dialogRef: MatDialogRef<AddUserDialogComponent>,
    public fb: FormBuilder,
    private mapsApiLoader: MapsAPILoader,
    public store: Store<fromRoot.State>,
    private toast: Toaster
  ) {
    super()
  }

  ngOnInit() {
    this.mapsApiLoader.load().then(() => {
      this.isGoogleLoaded = true
    })

    this.store.pipe(select(selectUsers), untilDestroy(this)).subscribe((users) => {
      this.users = users
    })

    this.store
      .pipe(select(selectActiveProductPlanView(ProductsForPlan.OwnerCenter)), untilDestroy(this))
      .subscribe((sub) => {
        this.isYearlyOC = sub?.isYearly
      })
  }

  close() {
    this.dialogRef.close()
  }

  onPlaceChange(parsedPlace: ParsedGooglePlace) {
    const country = R.find((c: any) => c.id === parsedPlace.countryShort, this.countryList)
    this.form.patchValue({
      address: (parsedPlace.streetNumber ? parsedPlace.streetNumber + ' ' : '') + (parsedPlace.street || ''),
      city: parsedPlace.city,
      state: parsedPlace.state,
      country: R.pathOr('', ['id'], country),
    })
  }

  @SaveForm()
  save(form: FormGroup) {
    const formValues = form.getRawValue() as UserSettingsForm
    if (isEmailTaken(formValues.email, this.users)) {
      this.toast.error('Email is already taken!')
      return
    }
    if (!R.equals(formValues.password1, formValues.password2)) {
      this.toast.error('Passwords do not match.')
      return
    }
    this.store.dispatch(AddUser({ form: formValues }))
    this.close()
  }
}
